/**
 * Finance Margin Page
 * 
 * Business Performance & Margin Forecasting
 * 
 * NON-NEGOTIABLE:
 * - Every number shows its source
 * - Missing data explicitly shown
 * - Advisory only, no prescriptions
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Wallet, 
  PiggyBank, 
  TrendingUp, 
  Receipt, 
  Upload, 
  Link2,
  BarChart3,
  AlertTriangle,
  Info
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { BusinessMarginPanel } from '../../components/MarginComponents';
import { AIPanel, AIButton, AIExplanationBox } from '../../components/AIComponents';

const financeSubNav = [
  { path: '/finance', label: 'Overview', icon: Wallet },
  { path: '/finance/pots', label: 'Money Pots', icon: PiggyBank },
  { path: '/finance/cashflow', label: 'Cashflow', icon: TrendingUp },
  { path: '/finance/invoices', label: 'Invoices', icon: Receipt },
  { path: '/finance/margin', label: 'Margin', icon: BarChart3 },
  { path: '/finance/suppliers', label: 'Supplier Upload', icon: Upload },
  { path: '/finance/xero', label: 'Xero', icon: Link2 },
];

export function Margin() {
  const location = useLocation();
  const { orgId } = useAuth();

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1>Margin Forecasting</h1>
          <p>Understand where your money goes</p>
        </div>
      </div>

      {/* Sub Navigation */}
      <div className="flex gap-sm mb-xl" style={{ flexWrap: 'wrap' }}>
        {financeSubNav.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`btn ${location.pathname === item.path ? 'btn-primary' : 'btn-secondary'}`}
            style={{ gap: 'var(--space-sm)' }}
          >
            <item.icon size={16} />
            {item.label}
          </Link>
        ))}
      </div>

      {/* Non-Negotiable Rules Notice */}
      <div 
        data-testid="margin-rules-notice"
        style={{
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-md)',
          marginBottom: 'var(--space-lg)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-sm)' }}>
          <Info size={18} style={{ color: 'rgb(59, 130, 246)', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>
              How This Works
            </div>
            <ul style={{ 
              margin: 0, 
              paddingLeft: 'var(--space-md)', 
              fontSize: '0.8125rem', 
              color: 'var(--text-secondary)',
              lineHeight: 1.6
            }}>
              <li><strong>Zero guessing:</strong> If data is missing, we say so. No invented numbers.</li>
              <li><strong>Source-locked:</strong> Every figure shows where it came from (Quote, Job, PO, Bank, Xero).</li>
              <li><strong>Advisory only:</strong> We suggest, never prescribe. You make the decisions.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* AI Business Advisor */}
      <AIPanel 
        title="AI Business Advisor" 
        description="AI explains why your business is performing the way it is - using real data only."
        className="mb-xl"
      >
        <div className="ai-actions-grid">
          <AIButton icon={AlertTriangle} tooltip="Coming soon">
            Why is my margin low?
          </AIButton>
          <AIButton icon={TrendingUp} tooltip="Coming soon">
            Am I making money?
          </AIButton>
          <AIButton icon={BarChart3} tooltip="Coming soon">
            Which jobs hurt profit?
          </AIButton>
        </div>
      </AIPanel>

      {/* Business Margin Panel */}
      {orgId && (
        <BusinessMarginPanel orgId={orgId} />
      )}

      {/* Explanation */}
      <AIExplanationBox className="mt-xl">
        Margin analysis uses your recorded data from Quotes, Jobs, Purchase Orders, and Xero. 
        The AI explains patterns - it never guesses prices or invents costs. 
        If something is unclear, it will tell you what data is missing.
      </AIExplanationBox>
    </div>
  );
}

export default Margin;
